import axios from "axios";
import store from "../Store";
import {
  setInProgress,
  setTimedOut,
  setLastRequestedTime,
  validateXSRFToken,
  setXSRFToken,
  setSystemUnavailable,
  setVersionIncompatible,
  setLoggedOut
} from "../actions/networkStateActions";
import { FORBIDDEN, UNAUTHORIZED } from "http-status-codes";
import {
  SystemUnavailable,
  B2CSessionTimeOutError,
  LoginStatus,
  UserStatus,
  PolicyType
} from "../constants/Constants";
import { getLayout} from "../layout/Layout";
import { SilentRedirectToLogin } from "../helpers/AccountsHelper";
import { setRedirectPath } from "../components/navigation/actions/siteRedirectActions";
import { Paths } from "../constants/UrlConstants";
import userManager from "../helpers/UserManagerHelper";
import { setExitSurveyUrlFromType } from "../components/logout/loggedOutActions";

function dispatchValidateXSRFTokenAction() {
  return store.dispatch(validateXSRFToken());
}
function dispatchSetXSRFTokenAction(token) {
  store.dispatch(setXSRFToken(token));
}
function dispatchToTimedOutAction() {
  store.dispatch(setTimedOut());
}
function dispatchLoadingToStore(value) {
  store.dispatch(setInProgress(value));
}
function dispatchLastRequestedTime(value) {
  store.dispatch(setLastRequestedTime(value));
}
function dispatchToSystemUnavailable() {
  store.dispatch(setSystemUnavailable(true));
}
function dispatchToVersionIncompatible() {
  store.dispatch(setVersionIncompatible());
}
function redirectToMemberDetails() {
  store.dispatch(setRedirectPath(Paths.MemberDetails));
}
async function redirectToLoggedOutIfNoUser() {
  let loadedUser = await userManager.getUser();
  if (!loadedUser) {
    let state = store.getState();
    let layout = state && state.layoutReducer && state.layoutReducer.layout;
    let isInternational =
      state &&
      state.policyReducer &&
      state.policyReducer.selectedPolicy &&
      state.policyReducer.selectedPolicy.IsInternational;
    let type = isInternational ? PolicyType.International : layout;
    store.dispatch(setExitSurveyUrlFromType(type));
    store.dispatch(setLoggedOut());
    store.dispatch(setRedirectPath(Paths.LoggedOut));
    throw axios.Cancel;
  }
}

const Network = {
  setupInterceptors: () => {
    axios.interceptors.request.use(
      async function (config) {
        if (!config.avoidSilentRenew) {
          await redirectToLoggedOutIfNoUser();

          var user = await SilentRedirectToLogin().catch((response) => {
            if (response === B2CSessionTimeOutError) {
              dispatchToTimedOutAction();
              throw axios.Cancel;
            }
          });

          const accessToken = user && user.access_token;

          if (accessToken) {
            config.headers.authorization = `Bearer ${accessToken}`;
          }
        }

        let token = dispatchValidateXSRFTokenAction();
        if (!token) {
          throw axios.Cancel;
        }

        config.headers.XSRF_TOKEN = token;
        if (window.axa) {
          config.headers.SYNCHRONIZATION_VERSION = window.axa.version;
        }

        config.params = {...config.params, 'brand-layout': getLayout()};

        dispatchLoadingToStore(!config.avoidLoadingFeature);

        return config;
      },
      function (error) {
        dispatchLoadingToStore(false);
        return Promise.reject(error);
      }
    );

    axios.interceptors.response.use(
      async function (response) {
        if (response.headers.xsrf_token) {
          dispatchSetXSRFTokenAction(response.headers.xsrf_token);
        }
        dispatchLastRequestedTime(new Date().getTime());
        dispatchLoadingToStore(false);

        if (response.headers.synchronization_version) {
          dispatchToVersionIncompatible();
        }

        return response;
      },
      function (error) {
        dispatchLoadingToStore(false);

        if (error.response) {
          if (error.response.headers) {
            if (error.response.headers.xsrf_token) {
              dispatchSetXSRFTokenAction(error.response.headers.xsrf_token);
            }
            if (error.response.headers.synchronization_version) {
              dispatchToVersionIncompatible();
            }
          }

          if (
            error.response.status === UNAUTHORIZED &&
            !(error.config && error.config.ignoreTimeout)
          ) {
            if (error.response.data && error.response.data.status) {
              if (!LoginStatus.hasOwnProperty(error.response.data.status)) {
                dispatchToTimedOutAction();
              }
            } else {
              dispatchToTimedOutAction();
            }
          }

          if (
            error.response.status === FORBIDDEN &&
            error.response.data &&
            error.response.data.status === SystemUnavailable
          ) {
            dispatchToSystemUnavailable();
          }

          if (
            error.response.status === FORBIDDEN &&
            error.response.data &&
            (error.response.data.status === UserStatus.Reset ||
              error.response.data.status === UserStatus.UserDoesNotExist)
          ) {
            redirectToMemberDetails();
          }
        }
        return Promise.reject(error);
      }
    );
  }
};

export default Network;
